// Componentes básicos (portados da POC). Use as classes CSS de styles.css para o resto (card, btn, badge, chip...).
import { cloneElement, useId, type ReactElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import type { Health } from '@impulso/shared'
import { pct } from '@impulso/shared'

export function PlatformLogo({ platform, size }: { platform: string; size?: 'lg' }) {
  const map: Record<string, string> = { google_ads: 'G', google: 'G', meta: 'M', whatsapp: 'W', shop: 'N', tiktok: 'T', openai_ads: 'O', fake: 'D' }
  const cls = platform === 'google_ads' ? 'google' : platform === 'fake' ? 'shop' : platform
  return <span className={`pf-logo pf-${cls} ${size === 'lg' ? 'lg' : ''}`}>{map[platform] ?? '?'}</span>
}

export function Platform({ platform, label }: { platform: string; label?: string }) {
  const names: Record<string, string> = { google_ads: 'Google', meta: 'Instagram/Facebook', tiktok: 'TikTok', openai_ads: 'OpenAI Ads', fake: 'Demonstração' }
  return (
    <span className="platform">
      <PlatformLogo platform={platform} />
      {label ?? names[platform] ?? platform}
    </span>
  )
}

const HEALTH: Record<Health, { cls: string; dot: string; text: string }> = {
  good: { cls: 'green', dot: '#16a34a', text: 'Indo bem' },
  attention: { cls: 'amber', dot: '#d97706', text: 'Precisa de atenção' },
  bad: { cls: 'red', dot: '#dc2626', text: 'Com problema' },
  paused: { cls: '', dot: '#9ca3af', text: 'Pausada' },
  draft: { cls: 'purple', dot: '#5b3df5', text: 'Rascunho' },
  learning: { cls: 'blue', dot: '#1d4ed8', text: 'Aprendendo' },
  no_data: { cls: '', dot: '#9ca3af', text: 'Sem dados ainda' },
}

export function HealthBadge({ value }: { value: Health }) {
  const h = HEALTH[value] ?? HEALTH.no_data
  return (
    <span className={`badge ${h.cls}`}>
      <span className="status-dot" style={{ background: h.dot }} />
      {h.text}
    </span>
  )
}

/** Variação percentual. Para custos, subir é ruim: passe invert. */
export function Delta({ value, invert }: { value: number | null | undefined; invert?: boolean }) {
  if (value == null || !isFinite(value)) return null
  // arredonda para 0%: sem seta nem cor de alerta
  if (Math.round(value * 100) === 0) return <span className="delta flat">0%</span>
  const good = invert ? value < 0 : value > 0
  const Icon = value > 0 ? ArrowUpRight : ArrowDownRight
  return (
    <span className={`delta ${good ? 'up' : 'down'}`}>
      <Icon size={13} />
      {pct(value)}
    </span>
  )
}

export function Switch({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={on} aria-label={label} disabled={disabled} className={`switch ${on ? 'on' : ''}`} onClick={() => onChange(!on)} />
}

export function Tabs<T extends string>({ value, onChange, options }: { value: T; onChange: (v: T) => void; options: Array<{ value: T; label: string }> }) {
  return (
    <div className="tabs" role="tablist">
      {options.map((o) => (
        <button key={o.value} type="button" role="tab" aria-selected={value === o.value} className={`tab ${value === o.value ? 'on' : ''}`} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function PageHead({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <p className="sub">{sub}</p>}
      </div>
      {children && <div className="actions">{children}</div>}
    </div>
  )
}

export function Loading({ lines = 3 }: { lines?: number }) {
  return (
    <div className="stack" aria-busy="true" aria-label="Carregando">
      {Array.from({ length: lines }).map((_, i) => <div key={i} className="skeleton" style={{ height: 18, width: `${90 - i * 12}%` }} />)}
    </div>
  )
}

/** Erros da API que têm um próximo passo claro para o usuário. */
function errorAction(error: unknown): { to: string; label: string } | null {
  const code = (error as { code?: string } | null)?.code
  if (code === 'totp_setup_required') return { to: '/app/perfil', label: 'Ativar verificação em duas etapas' }
  return null
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const msg = error instanceof Error ? error.message : 'Não foi possível carregar.'
  const action = errorAction(error)
  return (
    <div className="card empty" role="alert">
      <p>{msg}</p>
      {action ? <Link className="btn primary sm mt-12" to={action.to}>{action.label}</Link> : onRetry && <button className="btn sm mt-12" onClick={onRetry}>Tentar de novo</button>}
    </div>
  )
}

/**
 * Campo de formulário com o rótulo ligado ao controle (htmlFor/id) e dica/erro lidos por leitor de tela.
 * Uso: <Field label="Nome" hint="Como aparece no anúncio"><input className="input" .../></Field>
 * O filho recebe o `id` (use o seu, se já tiver um). Funciona com input, select, textarea e PasswordInput.
 */
export function Field({ label, hint, error, children }: { label: ReactNode; hint?: ReactNode; error?: ReactNode; children: ReactElement<{ id?: string; 'aria-describedby'?: string }> }) {
  const auto = useId()
  const id = children.props.id ?? auto
  const noteId = `${id}-nota`
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {cloneElement(children, { id, ...(hint || error ? { 'aria-describedby': noteId } : {}) })}
      {error ? <div id={noteId} className="form-error" role="alert">{error}</div> : hint ? <div id={noteId} className="small muted">{hint}</div> : null}
    </div>
  )
}

export function Empty({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="card empty">
      <h3 style={{ color: 'var(--text)' }}>{title}</h3>
      {children && <div className="mt-8">{children}</div>}
    </div>
  )
}

/** Placeholder usado pelas páginas ainda não implementadas. */
export function Placeholder({ title }: { title: string }) {
  return (
    <>
      <PageHead title={title} />
      <Empty title="Em construção">Esta tela será implementada pela fatia responsável.</Empty>
    </>
  )
}
